import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Phone, MapPin, Clock } from 'lucide-react';
import { API } from '../config/api';
import { useLanguage } from '../contexts/LanguageContext';

const ContactPage = () => {
  const { t, language } = useLanguage();
  const [venues, setVenues] = useState([]);
  const [settings, setSettings] = useState({});
  
  useEffect(() => {
    axios.get(`${API}/venues`).then(res => setVenues(res.data)).catch(console.error);
    axios.get(`${API}/settings/season`).then(res => setSettings(res.data)).catch(console.error);
  }, []);
  
  return (
    <div className="max-w-7xl mx-auto px-4 py-12" data-testid="contact-page">
      <div className="text-center mb-12">
        <h1 className="section-title mb-4">{t('contactTitle')}</h1>
        <p className="text-slate-600">{t('contactSubtitle')}</p>
      </div>

      <div className="grid md:grid-cols-2 gap-12">
        <div>
          {/* Google Maps Embed */}
          <div className="rounded-2xl overflow-hidden shadow-lg mb-8">
            <iframe 
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3141.8376712024!2d23.752892076465396!3d38.06376077191373!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14a1a2c9e4c97c7b%3A0x8c0a6f7c9c0a6f7c!2sKosta%20Varnali%2049%2C%20Metamorfosi%20144%2052%2C%20Greece!5e0!3m2!1sen!2sgr!4v1707500000000!5m2!1sen!2sgr"
              width="100%" 
              height="350" 
              style={{ border: 0 }} 
              allowFullScreen="" 
              loading="lazy" 
              referrerPolicy="no-referrer-when-downgrade"
              title="WBL Office Location"
            ></iframe>
          </div>
        </div>
        <div>
          <div className="card p-8">
            <h2 className="font-display font-bold text-2xl text-[#1E3A5F] mb-6">{t('leagueOffice')}</h2>
            
            <div className="space-y-6">
              <div className="flex items-start gap-4" data-testid="contact-address">
                <div className="w-12 h-12 rounded-full bg-[#F97316]/10 flex items-center justify-center flex-shrink-0">
                  <MapPin className="text-[#F97316]" size={24} />
                </div>
                <div>
                  <h3 className="font-semibold text-[#1E3A5F]">{t('address')}</h3>
                  <p className="text-slate-600">Κώστα Βάρναλη 49<br />Μεταμόρφωση, Αθήνα 14452<br />Ελλάδα</p>
                  <a 
                    href="https://www.google.com/maps/search/?api=1&query=Κώστα+Βάρναλη+49+Μεταμόρφωση+Αθήνα+14452" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-[#F97316] text-sm hover:underline mt-1 inline-block"
                  >
                    {language === 'gr' ? 'Δείτε στο χάρτη →' : 'View on map →'}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4" data-testid="contact-phone">
                <div className="w-12 h-12 rounded-full bg-[#F97316]/10 flex items-center justify-center flex-shrink-0">
                  <Phone className="text-[#F97316]" size={24} />
                </div>
                <div>
                  <h3 className="font-semibold text-[#1E3A5F]">{t('phone')}</h3>
                  <p className="text-slate-600">+30 6945187929</p>
                </div>
              </div>

              <div className="flex items-start gap-4" data-testid="contact-email">
                <div className="w-12 h-12 rounded-full bg-[#F97316]/10 flex items-center justify-center flex-shrink-0">
                  <svg className="text-[#F97316]" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                    <polyline points="22,6 12,13 2,6"/>
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-[#1E3A5F]">{t('email')}</h3>
                  <a href="mailto:info@wbl.gr" className="text-slate-600 hover:text-[#F97316]">info@wbl.gr</a>
                </div>
              </div>

              <div className="flex items-start gap-4" data-testid="contact-hours">
                <div className="w-12 h-12 rounded-full bg-[#F97316]/10 flex items-center justify-center flex-shrink-0">
                  <Clock className="text-[#F97316]" size={24} />
                </div>
                <div>
                  <h3 className="font-semibold text-[#1E3A5F]">{t('officeHours')}</h3>
                  <p className="text-slate-600">{t('monFri')}: 9:00 - 18:00<br />{t('saturday')}: 10:00 - 14:00</p>
                </div>
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-slate-200">
              <h3 className="font-semibold text-[#1E3A5F] mb-4">{t('followUs')}</h3>
              <div className="flex gap-3">
                <a href="https://www.facebook.com/share/1FkJdSWQAv/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-[#1877F2] text-white flex items-center justify-center hover:opacity-80 transition" data-testid="contact-facebook">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                </a>
                <a href="https://www.instagram.com/womensbasketleague_gr?igsh=MXFkNjY1eTRqMjBsaw%3D%3D&utm_source=qr" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-gradient-to-br from-[#833AB4] via-[#FD1D1D] to-[#F77737] text-white flex items-center justify-center hover:opacity-80 transition" data-testid="contact-instagram">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C16.67.014 16.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                </a>
                <a href={settings.youtube_url || 'https://www.youtube.com'} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-[#FF0000] text-white flex items-center justify-center hover:opacity-80 transition" data-testid="contact-youtube">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Venues Section - Dynamic */}
      {venues.length > 0 && (
        <div className="mt-12 space-y-6">
          <h2 className="text-2xl font-display font-bold text-[#1E3A5F] text-center mb-6">
            {language === 'gr' ? (venues.length > 1 ? 'Γήπεδα Αγώνων' : 'Γήπεδο Αγώνων') : (venues.length > 1 ? 'Game Venues' : 'Game Venue')}
          </h2>
          {venues.map((venue, idx) => (
            <div key={venue.id || idx} className="bg-gradient-to-r from-[#1E3A5F] to-[#2D5A87] rounded-2xl p-8 text-white" data-testid={`contact-venue-${idx}`}>
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <h4 className="text-xl text-[#F97316] font-bold mb-3">{venue.name}</h4>
                  {venue.description && <p className="text-white/80 mb-4">{venue.description}</p>}
                  {venue.address && (
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(venue.address)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-white text-[#1E3A5F] px-5 py-2.5 rounded-lg font-semibold hover:bg-slate-100 transition"
                      data-testid={`contact-venue-map-link-${idx}`}
                    >
                      <MapPin size={20} />
                      {language === 'gr' ? 'Δείτε στο Google Maps' : 'View on Google Maps'}
                    </a>
                  )}
                </div>
                {venue.map_embed && (
                  <div className="rounded-xl overflow-hidden shadow-lg">
                    <iframe src={venue.map_embed} width="100%" height="250" style={{ border: 0 }} allowFullScreen="" loading="lazy" referrerPolicy="no-referrer-when-downgrade" title={venue.name}></iframe>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ContactPage;
